import {
  GameActivity,
  GameSnapshot,
  GameState,
  getApiGamesByGameIdFullOptions,
  PlateAppearance as SnapshotPlateAppearance,
  ResultingState as SnapshotResultingState,
} from "@aura-frontend/wgs-client";
import { useQuery } from "@tanstack/react-query";

import {
  Activity,
  GameBaseRunners,
  GameFullGame,
  GameFullResponse,
  GamePlayerRef,
  HitData,
  PitchData,
  PlateAppearance,
  ResultingState,
} from "../types/gameFull";
import {
  getActivitiesForPA,
  getNewestActivity,
} from "../utils/plateAppearanceFeed";
import { parsePlateAppearanceId } from "../utils/plateAppearanceId";

/**
 * Game service GET /api/games/{gameId}/full (via the WIN API), normalized to
 * the UI's GameFullResponse. The generated types leave most fields optional;
 * defaults are filled in here so the components can stay as they are.
 */

/** Max PAs and activities per snapshot (the service caps it at 100). */
const SNAPSHOT_LIMIT = 100;

type SnapshotPlayerRef = { playerId?: string; playerName?: string };

const toPlayerRef = (ref?: SnapshotPlayerRef | null): GamePlayerRef | null =>
  ref?.playerId
    ? { playerId: ref.playerId, playerName: ref.playerName ?? "" }
    : null;

const UNKNOWN_PLAYER: GamePlayerRef = { playerId: "", playerName: "Unknown" };

// Runners come back as { first?: PlayerRef, … } under a loose type
const toBaseRunners = (runners?: Record<string, unknown>): GameBaseRunners => {
  const runner = (base: string) =>
    toPlayerRef(runners?.[base] as SnapshotPlayerRef | undefined) ?? undefined;
  return {
    first: runner("first"),
    second: runner("second"),
    third: runner("third"),
  };
};

const toResultingState = (state: SnapshotResultingState): ResultingState => ({
  inning: state.inning,
  isTopOfInning: state.isTopOfInning,
  outs: state.outs,
  balls: state.balls,
  strikes: state.strikes,
  score: { home: state.score.home ?? 0, away: state.score.away ?? 0 },
  baseRunners: toBaseRunners(state.baseRunners),
  currentHitter: toPlayerRef(state.currentHitter),
  currentPitcher: toPlayerRef(state.currentPitcher),
  plateAppearancesInHalfInning: state.plateAppearancesInHalfInning,
  outsRecorded: state.outsRecorded,
  runsScored: state.runsScored,
});

export const toPlateAppearance = (pa: SnapshotPlateAppearance) => ({
  plateAppearanceId: pa.plateAppearanceId,
  status: pa.status,
  inning: pa.inning,
  isTopOfInning: pa.isTopOfInning,
  hitter: toPlayerRef(pa.hitter) ?? UNKNOWN_PLAYER,
  pitcher: toPlayerRef(pa.pitcher) ?? UNKNOWN_PLAYER,
  activityIds: pa.activityIds,
  startedAt: pa.startedAt ?? "",
  endedAt: pa.endedAt,
  outcome: pa.outcome,
  runsScored: pa.runsScored,
});

/** Activities without pitch telemetry can't be shown in the feed. */
export const toActivity = (activity: GameActivity): Activity | null => {
  const { pitch, result } = activity;
  if (!pitch) return null;

  return {
    activityId: activity.activityId,
    machineUuid: activity.machineUuid ?? "",
    plateAppearanceId: activity.plateAppearanceId ?? "",
    pitch: {
      omnibusEventId: pitch.omnibusEventId,
      eventTime: pitch.eventTime,
      pitchData: pitch.pitchData as PitchData,
      hitData: (pitch.hitData as HitData | null | undefined) ?? null,
    },
    result: result && {
      pitchResult: result.pitchResult,
      playResult: result.playResult ?? null,
      outcome: result.outcome ?? null,
      contactType: result.contactType ?? null,
      occupiedBases: result.occupiedBases ?? null,
      pitchType: result.pitchType ?? null,
      pitcherSet: result.pitcherSet ?? null,
    },
    emissionCount: activity.emissionCount,
    tagCount: activity.tagCount,
    resultingState:
      activity.resultingState && toResultingState(activity.resultingState),
  };
};

export const toGameTeams = (
  teams: GameState["teams"],
): GameFullGame["teams"] => ({
  home: { ...teams.home, id: teams.home.id ?? "" },
  away: { ...teams.away, id: teams.away.id ?? "" },
});

export const toGame = (game: GameState): GameFullGame => ({
  gameId: game.gameId,
  displayName: game.displayName ?? "",
  // Omnibus states no sport for some games; V1 tags those as baseball too
  sport: game.sport ?? "baseball",
  inning: game.inning ?? 1,
  isTopOfInning: game.isTopOfInning ?? true,
  outs: game.outs ?? 0,
  balls: game.balls ?? 0,
  strikes: game.strikes ?? 0,
  baseRunners: toBaseRunners(game.baseRunners),
  currentHitter: toPlayerRef(game.currentHitter),
  currentPitcher: toPlayerRef(game.currentPitcher),
  currentPlateAppearanceId: game.currentPlateAppearanceId ?? null,
  halfInningInProgress: game.halfInningInProgress,
  plateAppearancesInHalfInning: game.plateAppearancesInHalfInning,
  gameStatus: game.gameStatus,
  teams: toGameTeams(game.teams),
  score: { home: game.score?.home ?? 0, away: game.score?.away ?? 0 },
  ...(game.times?.endTime && { endTime: game.times.endTime }),
});

/**
 * The id the service will give the next plate appearance, minted the way it
 * does (win-game-service src/domain/plateAppearanceId.ts): in the current
 * half if one is underway, else as the first of the next half.
 */
const getExpectedPlateAppearanceId = ({
  gameId,
  gameStatus,
  inning,
  isTopOfInning,
  halfInningInProgress,
  plateAppearancesInHalfInning,
}: GameFullGame) => {
  if (gameStatus !== "in-progress") return undefined;
  if (halfInningInProgress) {
    const half = isTopOfInning ? "top" : "bot";
    return `${gameId}-i${inning}-${half}-pa${plateAppearancesInHalfInning ?? 0}`;
  }
  if (inning === 0) return `${gameId}-i1-top-pa0`;
  return isTopOfInning
    ? `${gameId}-i${inning}-bot-pa0`
    : `${gameId}-i${inning + 1}-top-pa0`;
};

/**
 * The service only commits a pitch to the game once it's tagged, so between
 * at bats (after one ends, at the start of a half or the game) there is no
 * current plate appearance. This seats the next one, under the id the service
 * will give it (and its untagged pitches already carry), as the current one,
 * so the active at bat shows its waiting row and pitches and they can be
 * tagged. The service's own plate appearance replaces it once it starts.
 */
export const withPendingPlateAppearance = (
  data: GameFullResponse,
): GameFullResponse => {
  if (data.game.currentPlateAppearanceId) return data;

  // Untagged pitches carry this same id; the newest pitch's stands in when
  // the game state doesn't say (e.g. an older service)
  const newest = getNewestActivity(data.activities);
  const plateAppearanceId =
    getExpectedPlateAppearanceId(data.game) ??
    (newest && !newest.result ? newest.plateAppearanceId : undefined);
  if (!plateAppearanceId) return data;

  const existing = data.plateAppearances.find(
    (pa) => pa.plateAppearanceId === plateAppearanceId,
  );
  if (existing?.status === "ended") return data;

  const game = { ...data.game, currentPlateAppearanceId: plateAppearanceId };
  if (existing) return { ...data, game };

  const pitches = getActivitiesForPA(data, plateAppearanceId);
  const idParts = parsePlateAppearanceId(plateAppearanceId);
  const pending: PlateAppearance = {
    plateAppearanceId,
    status: "started",
    inning: idParts?.inning ?? data.game.inning,
    isTopOfInning: idParts?.isTopOfInning ?? data.game.isTopOfInning,
    hitter: data.game.currentHitter ?? UNKNOWN_PLAYER,
    pitcher: data.game.currentPitcher ?? UNKNOWN_PLAYER,
    activityIds: pitches.map((activity) => activity.activityId),
    isPending: true,
    startedAt: pitches[0]
      ? new Date(pitches[0].pitch.eventTime * 1000).toISOString()
      : new Date().toISOString(),
  };
  return {
    ...data,
    game,
    plateAppearances: [pending, ...data.plateAppearances],
  };
};

/**
 * A pending plate appearance can be overtaken before the service starts it:
 * the third out's game update still names the next batter in the same half,
 * then the half rolls over and the next half's first at bat is seated
 * instead. Pitches released into the overtaken one would be stranded there
 * (it's never current, so the feed doesn't show it), so this moves them into
 * the current at bat and drops it.
 */
export const withOvertakenPendingMerged = (
  data: GameFullResponse,
): GameFullResponse => {
  const current = data.plateAppearances.find(
    (pa) => pa.plateAppearanceId === data.game.currentPlateAppearanceId,
  );
  if (!current || current.status === "ended") return data;

  const overtaken = data.plateAppearances.filter(
    (pa) => pa.isPending && pa !== current,
  );
  if (overtaken.length === 0) return data;

  const overtakenIds = new Set(overtaken.map((pa) => pa.plateAppearanceId));
  const moved = data.activities
    .filter((activity) => overtakenIds.has(activity.plateAppearanceId))
    .sort((a, b) => a.pitch.eventTime - b.pitch.eventTime);
  const movedIds = moved
    .map((activity) => activity.activityId)
    .filter((id) => !current.activityIds.includes(id));
  return {
    ...data,
    activities: data.activities.map((activity) =>
      overtakenIds.has(activity.plateAppearanceId)
        ? { ...activity, plateAppearanceId: current.plateAppearanceId }
        : activity,
    ),
    plateAppearances: data.plateAppearances
      .filter((pa) => !overtakenIds.has(pa.plateAppearanceId))
      .map((pa) =>
        pa === current
          ? { ...pa, activityIds: [...movedIds, ...pa.activityIds] }
          : pa,
      ),
  };
};

export const toGameFullResponse = ({
  game,
  lineUp,
  plateAppearances,
  activities,
}: GameSnapshot): GameFullResponse =>
  withPendingPlateAppearance({
    game: toGame(game),
    lineUp,
    plateAppearances: plateAppearances.map(toPlateAppearance),
    activities: activities
      .map(toActivity)
      .filter((activity): activity is Activity => activity !== null),
  });

/** Links to the pages older than the snapshot's; null when it has them all. */
export type SnapshotNext = GameSnapshot["next"];

// Module scope so react-query keeps the selected result between renders
const selectSnapshot = (snapshot: GameSnapshot) => ({
  full: toGameFullResponse(snapshot),
  next: snapshot.next,
});

/**
 * The game's snapshot. Fetched once each time the page opens: the WebSocket
 * keeps it current after, and refetching would clobber the local-only edits
 * (pitch tags) until those save to omnibus. A cached one from an earlier
 * visit is behind the game, so opening the page fetches it again.
 */
export const useGameSnapshot = (gameId: string | undefined) =>
  useQuery({
    ...getApiGamesByGameIdFullOptions({
      path: { gameId: gameId ?? "" },
      query: { limit: SNAPSHOT_LIMIT },
    }),
    enabled: !!gameId,
    select: selectSnapshot,
    staleTime: Infinity,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
